import { PaymentGoalContext } from '../../../../../../payment-goal-context';
import {formatReferenceCopy} from 'utils/typography';
import CommissionHelp from '../commission-help';
import {COLORS} from 'config/constants';
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useFormContext, Controller} from "react-hook-form";

import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";

import {checkDecimalsAfterDot} from "../../../../../../../../../../../utils/validation";
import { ADD_PAYMENT_FORM, AddParamsPaymentForm } from "../../index";
import {ADORNMENTS} from "../../../../../../../../../../../config/constants";
import {useFormattedDecimalInput} from "../../../../../../../../../../../utils/hooks";
import MenuButton from "../../../../../../../../../../common/menuButton";

import './payment-item.scss';

type Props = {
    control: any;
    index: number
    invoiceAmount: number
    isFirstItem?: boolean
    remove?(id: number): void
}

const PaymentItem = ({ index, control, isFirstItem, invoiceAmount, remove }: Props) => {

  const { t } = useTranslation();

  const copy = (key: string) => formatReferenceCopy(t(key));

  const { register, formState, setValue, getValues, watch, setError, clearErrors, trigger } = useFormContext<AddParamsPaymentForm>()

  const [ isCopiedBankCommissionForTheTransferData, setIsCopiedBankCommissionForTheTransferData ] = useState(false);
  const [ isCopiedCurrencyControlCommissionData, setIsCopiedCurrencyControlCommissionData ] = useState(false);

  const { report } = React.useContext(PaymentGoalContext);

  useEffect(() => {
    const reportPayment = () => {
      const paymentValues = getValues(`payments.${index}` as any) as unknown as AddParamsPaymentForm;

      report({
        commission_percentage: paymentValues?.commission_percentage,
        payment_amount: paymentValues?.payment_amount,
        bank_transfer_fee: paymentValues?.bank_transfer_fee,
        min_bank_transfer_fee: paymentValues?.min_bank_transfer_fee,
        max_bank_transfer_fee: paymentValues?.max_bank_transfer_fee,
        currency_control_fee: paymentValues?.currency_control_fee,
        min_currency_control_fee: paymentValues?.min_currency_control_fee,
        max_currency_control_fee: paymentValues?.max_currency_control_fee,
        ...(!isFirstItem ? {
          copy_bank_transfer_fee: isCopiedBankCommissionForTheTransferData,
          copy_currency_control_fee: isCopiedCurrencyControlCommissionData,
        } : {}),
      });
    };

    reportPayment();
    const subscription = watch(reportPayment);

    return () => subscription.unsubscribe();
  }, [ report, getValues, watch, index, isFirstItem, isCopiedBankCommissionForTheTransferData, isCopiedCurrencyControlCommissionData ]);

  const decimalInput = useFormattedDecimalInput();

  // @ts-ignore
  const [ currentPercent, setCurrentPercent ] = useState(getValues(`payments.${index-1}.${ADD_PAYMENT_FORM.COMMISSION_PERCENTAGE}`));
  // @ts-ignore
  const [ currentAmount, setCurrentAmount ] = useState(getValues(`payments.${index-1}.${ADD_PAYMENT_FORM.PAYMENT_AMOUNT}`));

  const { errors } = formState;

  const watchedFieldsBankCommissionForTheTransfer = [ ADD_PAYMENT_FORM.BANK_TRANSFER_FEE, ADD_PAYMENT_FORM.MIN_BANK_TRANSFER_FEE, ADD_PAYMENT_FORM.MAX_BANK_TRANSFER_FEE ];

  const watchedFieldsCurrencyControlCommission = [ ADD_PAYMENT_FORM.CURRENCY_CONTROL_FEE, ADD_PAYMENT_FORM.MIN_CURRENCY_CONTROL_FEE, ADD_PAYMENT_FORM.MAX_CURRENCY_CONTROL_FEE ];

  watch((_, { name }) => {
    const shouldChangeCopiedBankCommissionForTheTransfer =
        name === `payments.${index-1}.${ADD_PAYMENT_FORM.BANK_TRANSFER_FEE}` ||
        name === `payments.${index-1}.${ADD_PAYMENT_FORM.MIN_BANK_TRANSFER_FEE}` ||
        name === `payments.${index-1}.${ADD_PAYMENT_FORM.MAX_BANK_TRANSFER_FEE}`

    const shouldChangeCopiedCurrencyControlCommission =
        name === `payments.${index-1}.${ADD_PAYMENT_FORM.CURRENCY_CONTROL_FEE}` ||
        name === `payments.${index-1}.${ADD_PAYMENT_FORM.MIN_CURRENCY_CONTROL_FEE}` ||
        name === `payments.${index-1}.${ADD_PAYMENT_FORM.MAX_CURRENCY_CONTROL_FEE}`

    if (
      shouldChangeCopiedBankCommissionForTheTransfer && isCopiedBankCommissionForTheTransferData ||
        shouldChangeCopiedCurrencyControlCommission && isCopiedCurrencyControlCommissionData
    ) {
      const fieldNames = name.split('.');
      const fieldName = fieldNames?.[fieldNames.length - 1];
      // @ts-ignore
      setValue(`payments.${index}.${fieldName}`, getValues(name))
    }
  });

  const errorNumber = copy('errors__message__incorrectValue');

  const copyBankCommissionForTheTransferData = (event: React.SyntheticEvent<Element, Event>, checked: boolean) => {
    watchedFieldsBankCommissionForTheTransfer.map((field) => {
      // @ts-ignore
      setValue(`payments.${index}.${field}`, checked ? getValues(`payments.${index-1}.${field}`) : undefined)
    })
    setIsCopiedBankCommissionForTheTransferData(checked);
  }

  const copyCurrencyControlCommissionData = (event: React.SyntheticEvent<Element, Event>, checked: boolean) => {
    watchedFieldsCurrencyControlCommission.map((field) => {
      // @ts-ignore
      setValue(`payments.${index}.${field}`, checked ? getValues(`payments.${index-1}.${field}`) : undefined)
    })
    setIsCopiedCurrencyControlCommissionData(checked);
  }


  const handleChangeCommissionPercentage = (rawValue: string) => {

    const value = Number(rawValue);
    const isErrorValue = value < 0 || value > 100;

    if (isErrorValue){
      // @ts-ignore
      setError(`payments.${index}.${ADD_PAYMENT_FORM.COMMISSION_PERCENTAGE}`, { type: "custom",  message: copy('errors__message__incorrectValue') });
    } else {
      // @ts-ignore
      clearErrors([ `payments.${index}.${ADD_PAYMENT_FORM.COMMISSION_PERCENTAGE}` ])
    }

    const amount = (invoiceAmount * value) / 100;

    setValue(
      // @ts-ignore
      `payments.${index}.${ADD_PAYMENT_FORM.PAYMENT_AMOUNT}`,
      amount.toFixed(2),
      { shouldValidate: true }
    );

    // @ts-ignore
    trigger(`payments.${index}.${ADD_PAYMENT_FORM.PAYMENT_AMOUNT}`);

  }

  const handleChangePaymentAmount = (rawValue: string) => {
    const value = Number(rawValue.replace(',', '.'));
    const isErrorValue = value < 0 || value > invoiceAmount;

    if (isErrorValue || checkDecimalsAfterDot(value, errorNumber) !== true){
      // @ts-ignore
      setError(`payments.${index}.${ADD_PAYMENT_FORM.PAYMENT_AMOUNT}`, { type: "custom",  message: copy('errors__message__incorrectValue') });
    } else {
      // @ts-ignore
      clearErrors([ `payments.${index}.${ADD_PAYMENT_FORM.PAYMENT_AMOUNT}` ])
    }

    const percentage = (value / invoiceAmount * 100).toFixed(2);

    // @ts-ignore
    setValue(`payments.${index}.${ADD_PAYMENT_FORM.COMMISSION_PERCENTAGE}`, percentage);

    // @ts-ignore
    setCurrentAmount(value.toString());
    // @ts-ignore
    trigger(`payments.${index}.${ADD_PAYMENT_FORM.COMMISSION_PERCENTAGE}`);

  }


  const menuOptions = [];

  if (remove){
    menuOptions.push({
      title: copy('actions__text__delete'),
      onClick: () => remove(index),
    })
  }

  return <div className='payment-item'>
    {menuOptions.length > 0 && (
      <div className='payment-item__menuButtons'>
        <MenuButton options={menuOptions}/>
      </div>
    )}
    <Typography variant='subtitle1' sx={{
      color: COLORS.BLACK_DARK,
      fontSize: '16px',
      fontWeight: 700,
      lineHeight: '24px',
      letterSpacing: 0,
      paddingRight: '48px',
    }}>{copy('calc__text__bankCommissions__invoicePayment')}</Typography>
    <div className='payment-item__form-row'>
      <Controller
        control={control}
        name={`payments.${index}.${ADD_PAYMENT_FORM.COMMISSION_PERCENTAGE}`}
        rules={{
          required: copy('common__text__isRequired'),
          validate: (value: string) =>
            checkDecimalsAfterDot(value, errorNumber),
        }}
        render={({ field, fieldState }) => (
          <TextField
            value={decimalInput.getDisplayValue({
              fieldName: `payments.${index}.${ADD_PAYMENT_FORM.COMMISSION_PERCENTAGE}`,
              rawValue: field.value,
            })}
            onChange={(e) => {
              decimalInput.startEditing(`payments.${index}.${ADD_PAYMENT_FORM.COMMISSION_PERCENTAGE}`);
              const value = e.target.value;
              const valueWithDot = value.replace(',', '.');
              field.onChange(valueWithDot);
              handleChangeCommissionPercentage(valueWithDot);
            }}
            onBlur={() => {
              field.onBlur();
              decimalInput.stopEditing(
                `payments.${index}.${ADD_PAYMENT_FORM.COMMISSION_PERCENTAGE}`
              );
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            variant='outlined'
            label={`${copy('calc__text__bankCommissions__payment')}, ${copy('calc__text__bankCommissions__percentages')}`}
            InputLabelProps={{ shrink: true }}
            fullWidth
            InputProps={{
              endAdornment: ADORNMENTS.PERCENT,
            }}
          />
        )}
      />
      <Controller
        control={control}
        name={`payments.${index}.${ADD_PAYMENT_FORM.PAYMENT_AMOUNT}`}
        rules={{
          required: copy('common__text__isRequired'),
          validate: (value: string) => checkDecimalsAfterDot(value, errorNumber, 2),
        }}
        render={({ field, fieldState }) => (
          <TextField
            value={decimalInput.getDisplayValue({
              fieldName: `payments.${index}.${ADD_PAYMENT_FORM.PAYMENT_AMOUNT}`,
              rawValue: field.value,
            })}
            onChange={(e) => {
              decimalInput.startEditing(`payments.${index}.${ADD_PAYMENT_FORM.PAYMENT_AMOUNT}`);
              const value = e.target.value;
              const valueWithDot = value.replace(',', '.');
              field.onChange(valueWithDot);
              handleChangePaymentAmount(valueWithDot);
            }}
            onBlur={() => {
              field.onBlur();
              decimalInput.stopEditing(
                `payments.${index}.${ADD_PAYMENT_FORM.PAYMENT_AMOUNT}`
              );
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            variant='outlined'
            label={`${copy('calc__text__bankCommissions__payment')}, ${copy('calc__text__bankCommissions__amount')}`}
            InputLabelProps={{ shrink: true }}
            fullWidth
            InputProps={{
              endAdornment: ADORNMENTS.PRICE,
            }}
          />
        )}
      />
    </div>
    <Typography variant='subtitle1' sx={{
      color: COLORS.BLACK_DARK,
      fontSize: '16px',
      fontWeight: 700,
      lineHeight: '24px',
      letterSpacing: 0,
      marginBottom: '8px',
    }}>{copy('calc__text__bankCommissions__bankCommissionForTheTransfer')}</Typography>
    <CommissionHelp />
    {!isFirstItem && (
      <FormGroup>
        <FormControlLabel
          checked={isCopiedBankCommissionForTheTransferData}
          onChange={copyBankCommissionForTheTransferData}
          control={<Checkbox/>}
          label={copy('calc__text__bankCommissions__copyDataFromPreviousPayment')}
        />
      </FormGroup>
    )}

    <div className='payment-item__form-row'>
      <Controller
        control={control}
        name={`payments.${index}.${ADD_PAYMENT_FORM.BANK_TRANSFER_FEE}`}
        rules={{
          required: copy('common__text__isRequired'),
          validate: (value: string) => checkDecimalsAfterDot(value, errorNumber, 2),
        }}
        render={({ field, fieldState }) => (
          <TextField
            value={decimalInput.getDisplayValue({
              fieldName: `payments.${index}.${ADD_PAYMENT_FORM.BANK_TRANSFER_FEE}`,
              rawValue: field.value,
            })}
            onChange={(e) => {
              decimalInput.startEditing(`payments.${index}.${ADD_PAYMENT_FORM.BANK_TRANSFER_FEE}`);
              const value = e.target.value;
              const valueWithDot = value.replace(',', '.');
              field.onChange(valueWithDot);
            }}
            onBlur={() => {
              field.onBlur();
              decimalInput.stopEditing(
                `payments.${index}.${ADD_PAYMENT_FORM.BANK_TRANSFER_FEE}`
              );
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            variant='outlined'
            label={copy('calc__text__bankCommissions__commission')}
            InputLabelProps={{ shrink: true }}
            fullWidth
            InputProps={{
              endAdornment: ADORNMENTS.PERCENT,
            }}
          />
        )}
      />
      <Controller
        control={control}
        name={`payments.${index}.${ADD_PAYMENT_FORM.MIN_BANK_TRANSFER_FEE}`}
        rules={{
          validate: (value: string) =>
            value ? checkDecimalsAfterDot(value, errorNumber, 2): true,
        }}
        render={({ field, fieldState }) => (
          <TextField
            value={decimalInput.getDisplayValue({
              fieldName: `payments.${index}.${ADD_PAYMENT_FORM.MIN_BANK_TRANSFER_FEE}`,
              rawValue: field.value,
            })}
            onChange={(e) => {
              decimalInput.startEditing(`payments.${index}.${ADD_PAYMENT_FORM.MIN_BANK_TRANSFER_FEE}`);
              const value = e.target.value;
              field.onChange(value.replace(',', '.'));
            }}
            onBlur={() => {
              field.onBlur();
              decimalInput.stopEditing(
                `payments.${index}.${ADD_PAYMENT_FORM.MIN_BANK_TRANSFER_FEE}`
              );
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            variant='outlined'
            label={copy('calc__text__bankCommissions__minCommission')}
            InputLabelProps={{ shrink: true }}
            fullWidth
            InputProps={{
              endAdornment: ADORNMENTS.PRICE,
            }}
          />
        )}
      />
      <Controller
        control={control}
        name={`payments.${index}.${ADD_PAYMENT_FORM.MAX_BANK_TRANSFER_FEE}`}
        rules={{
          validate: (value: string) => value ? checkDecimalsAfterDot(value, errorNumber, 2): true,
        }}
        render={({ field, fieldState }) => (
          <TextField
            value={decimalInput.getDisplayValue({
              fieldName: `payments.${index}.${ADD_PAYMENT_FORM.MAX_BANK_TRANSFER_FEE}`,
              rawValue: field.value,
            })}
            onChange={(e) => {
              decimalInput.startEditing(`payments.${index}.${ADD_PAYMENT_FORM.MAX_BANK_TRANSFER_FEE}`);
              const value = e.target.value;
              field.onChange(value.replace(',', '.'));
            }}
            onBlur={() => {
              field.onBlur();
              decimalInput.stopEditing(
                `payments.${index}.${ADD_PAYMENT_FORM.MAX_BANK_TRANSFER_FEE}`
              );
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            variant='outlined'
            label={copy('calc__text__bankCommissions__maxCommission')}
            InputLabelProps={{ shrink: true }}
            fullWidth
            InputProps={{
              endAdornment: ADORNMENTS.PRICE,
            }}
          />
        )}
      />
    </div>
    <Typography variant='subtitle1' sx={{
      color: COLORS.BLACK_DARK,
      fontSize: '16px',
      fontWeight: 700,
      lineHeight: '24px',
      letterSpacing: 0,
      marginBottom: '8px',
    }}>{copy('calc__text__bankCommissions__currencyControlCommission')}</Typography>
    <CommissionHelp isCurrencyControl />
    {!isFirstItem && (
      <FormGroup>
        <FormControlLabel
          checked={isCopiedCurrencyControlCommissionData}
          onChange={copyCurrencyControlCommissionData}
          control={<Checkbox/>}
          label={copy('calc__text__bankCommissions__copyDataFromPreviousPayment')}
        />
      </FormGroup>
    )}
    <div className='payment-item__form-row'>
      <Controller
        control={control}
        name={`payments.${index}.${ADD_PAYMENT_FORM.CURRENCY_CONTROL_FEE}`}
        rules={{
          required: copy('common__text__isRequired'),
          validate: (value: string) => checkDecimalsAfterDot(value, errorNumber, 2),
        }}
        render={({ field, fieldState }) => (
          <TextField
            value={decimalInput.getDisplayValue({
              fieldName: `payments.${index}.${ADD_PAYMENT_FORM.CURRENCY_CONTROL_FEE}`,
              rawValue: field.value,
            })}
            onChange={(e) => {
              decimalInput.startEditing(`payments.${index}.${ADD_PAYMENT_FORM.CURRENCY_CONTROL_FEE}`);
              const value = e.target.value;
              field.onChange(value.replace(',', '.'));
            }}
            onBlur={() => {
              field.onBlur();
              decimalInput.stopEditing(
                `payments.${index}.${ADD_PAYMENT_FORM.CURRENCY_CONTROL_FEE}`
              );
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            variant='outlined'
            label={copy('calc__text__bankCommissions__commission')}
            InputLabelProps={{ shrink: true }}
            fullWidth
            InputProps={{
              endAdornment: ADORNMENTS.PERCENT,
            }}
          />
        )}
      />
      <Controller
        control={control}
        name={`payments.${index}.${ADD_PAYMENT_FORM.MIN_CURRENCY_CONTROL_FEE}`}
        rules={{
          validate: (value: string) => value ? checkDecimalsAfterDot(value, errorNumber, 2) : true,
        }}
        render={({ field, fieldState }) => (
          <TextField
            value={decimalInput.getDisplayValue({
              fieldName: `payments.${index}.${ADD_PAYMENT_FORM.MIN_CURRENCY_CONTROL_FEE}`,
              rawValue: field.value,
            })}
            onChange={(e) => {
              decimalInput.startEditing(`payments.${index}.${ADD_PAYMENT_FORM.MIN_CURRENCY_CONTROL_FEE}`);
              const value = e.target.value;
              field.onChange(value.replace(',', '.'));
            }}
            onBlur={() => {
              field.onBlur();
              decimalInput.stopEditing(
                `payments.${index}.${ADD_PAYMENT_FORM.MIN_CURRENCY_CONTROL_FEE}`
              );
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            variant='outlined'
            label={copy('calc__text__bankCommissions__minCommission')}
            InputLabelProps={{ shrink: true }}
            fullWidth
            InputProps={{
              endAdornment: ADORNMENTS.PRICE,
            }}
          />
        )}
      />

      <Controller
        control={control}
        name={`payments.${index}.${ADD_PAYMENT_FORM.MAX_CURRENCY_CONTROL_FEE}`}
        rules={{
          validate: (value: string) => value ? checkDecimalsAfterDot(value, errorNumber, 2) : true,
        }}
        render={({ field, fieldState }) => (
          <TextField
            value={decimalInput.getDisplayValue({
              fieldName: `payments.${index}.${ADD_PAYMENT_FORM.MAX_CURRENCY_CONTROL_FEE}`,
              rawValue: field.value,
            })}
            onChange={(e) => {
              decimalInput.startEditing(`payments.${index}.${ADD_PAYMENT_FORM.MAX_CURRENCY_CONTROL_FEE}`);
              const value = e.target.value;
              field.onChange(value.replace(',', '.'));
            }}
            onBlur={() => {
              field.onBlur();
              decimalInput.stopEditing(
                `payments.${index}.${ADD_PAYMENT_FORM.MAX_CURRENCY_CONTROL_FEE}`
              );
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            variant='outlined'
            label={copy('calc__text__bankCommissions__maxCommission')}
            InputLabelProps={{ shrink: true }}
            fullWidth
            InputProps={{
              endAdornment: ADORNMENTS.PRICE,
            }}
          />
        )}
      />

    </div>
  </div>
}

export default PaymentItem
